import React, { createContext, useContext, useEffect, useState } from 'react';
import { products } from '../mock';
import { cartLineKey, DEFAULT_SIZE, isSizeAvailable, normalizeSize } from '../constants/bottleSizes';
import { fbTrack } from '../lib/fbpixel';

const CartContext = createContext(null);

/** Enrich older cart rows that were saved without slug/shots/size */
function hydrateCartItem(item) {
  if (!item?.id) return item;
  const size = normalizeSize(item.size);
  const lineKey = item.lineKey || cartLineKey(item.id, size);
  const p = products.find((x) => x.id === item.id);
  return {
    ...item,
    size,
    lineKey,
    slug: item.slug || p?.slug,
    shots: item.shots?.length ? item.shots : p?.shots || [],
    accent: item.accent || p?.accent,
    labelTint: item.labelTint || p?.labelTint,
    code: item.code || p?.code,
  };
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem('rp_cart');
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.map(hydrateCartItem) : [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem('rp_cart', JSON.stringify(items));
  }, [items]);

  const addItem = (product, qty = 1, size = DEFAULT_SIZE) => {
    const chosen = normalizeSize(size);
    if (!isSizeAvailable(chosen)) return;
    const left = product.stock == null ? null : Number(product.stock);
    if (left === 0) return;
    const lineKey = cartLineKey(product.id, chosen);
    fbTrack('AddToCart', {
      content_name: product.name,
      content_ids: [product.id],
      content_type: 'product',
      value: (product.price || 0) * qty,
      currency: 'INR',
    });
    setItems((prev) => {
      const existing = prev.find((p) => p.lineKey === lineKey);
      const nextQty = (existing ? existing.qty : 0) + qty;
      const capped = left != null && Number.isFinite(left) ? Math.min(nextQty, left) : nextQty;
      if (capped < 1) return prev;
      if (existing) return prev.map((p) => (p.lineKey === lineKey ? { ...p, qty: Math.max(1, capped) } : p));
      return [
        ...prev,
        {
          id: product.id,
          lineKey,
          size: chosen,
          name: product.name,
          price: product.price,
          mrp: product.mrp,
          code: product.code,
          slug: product.slug,
          shots: product.shots || [],
          accent: product.accent,
          labelTint: product.labelTint,
          qty: left != null && Number.isFinite(left) ? Math.min(qty, left) : qty,
        },
      ];
    });
    setOpen(true);
  };
  const removeItem = (lineKey) => setItems((prev) => prev.filter((p) => p.lineKey !== lineKey && p.id !== lineKey));
  const updateQty = (lineKey, qty) =>
    setItems((prev) =>
      prev.map((p) => (p.lineKey === lineKey || p.id === lineKey ? { ...p, qty: Math.max(1, qty) } : p))
    );
  const clear = () => setItems([]);
  const total = items.reduce((s, i) => s + i.price * i.qty, 0);
  const count = items.reduce((s, i) => s + i.qty, 0);

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, updateQty, clear, total, count, open, setOpen }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
